import {XText, XCol} from "../../components/xui";
import {
  ArrowOverlay,
  DropDownForChild,
  TooltipForChild,
  useGlobalKeyPress,
  useToggle,
} from "@cdx/common";
import {PotionIcon} from "../../components/Card/PotionIcon";
import useMutation from "../../lib/hooks/useMutation";
import {getUpvoteInfo, UpvoteTooltip} from "../upvotes/upvote-utils";
import {forwardRef, useEffect} from "react";
import CreateAccountOverlay from "../public-project/CreateAccountOverlay";
import {useLocalStorageState} from "../../lib/storage";
import {JoinAsLoggedInUser} from "../public-project/CommunityButton";
import messenger from "../../lib/messenger";
import {DSIconButton} from "@cdx/ds";

const UpvoteButton = forwardRef(
  ({onClick, disabled, upvoteInfo, card, active, hideTooltip}, ref) => {
    const isUpvoted = Boolean(upvoteInfo.myUpvoteId);
    useGlobalKeyPress({
      key: "u",
      fn: () => {
        const res = onClick();
        if (res) {
          return res.then(() => messenger.send(isUpvoted ? "Upvote removed" : "Card upvoted"));
        }
      },
      description: "Upvote opened card",
      category: "Card detail view",
    });
    return (
      <TooltipForChild
        tooltip={!hideTooltip && (() => <UpvoteTooltip upvoteInfo={upvoteInfo} card={card} />)}
        bg="gray700"
        targetIsDisabled={disabled}
        ref={ref}
      >
        <DSIconButton
          size="sm"
          variant="tertiary"
          negatePadding
          icon={<PotionIcon active={active} progress={Math.min(1, upvoteInfo.totalCount / 20)} />}
          label={upvoteInfo.totalCount}
          disabled={disabled}
          onClick={onClick}
        />
      </TooltipForChild>
    );
  }
);

const VoteConfirm = ({closeJoinedHint}) => {
  useEffect(() => {
    return () => {
      closeJoinedHint();
    };
  }, [closeJoinedHint]);
  return (
    <XCol px={3} py={2}>
      <XText color="white" preset="bold">
        You may now vote!
      </XText>
    </XCol>
  );
};

const NonCommunityMemberUpvoteButton = ({
  upvoteInfo,
  card,
  user,
  projectId,
  justJoined,
  onJoined,
  closeJoinedHint,
  onVote,
  cardCtx,
}) => {
  const [hintInfo, setHintInfo] = useLocalStorageState(
    `openProject.voteHintInfo`,
    {seenIn: {}},
    {
      type: "session",
    }
  );

  const seeHint = (() => {
    if (hintInfo.seenIn[cardCtx]) return false;
    if (Object.keys(hintInfo.seenIn).length > 3) return false;
    return true;
  })();

  useEffect(() => {
    const id = setTimeout(
      () => setHintInfo((prev) => ({seenIn: {...prev.seenIn, [cardCtx]: true}})),
      2000
    );
    return () => clearTimeout(id);
  }, [cardCtx, setHintInfo]);

  return (
    <DropDownForChild
      initialOpen={seeHint}
      overlayProps={{placement: "top", distanceFromAnchor: 10}}
      setChildProps={({isOpen, close}) => ({
        hideTooltip: isOpen,
        ...(justJoined && {
          onClick: () => {
            close();
            closeJoinedHint();
            return onVote();
          },
        }),
      })}
      renderOverlay={({close, ...props}) => (
        <ArrowOverlay bg="purple700" {...props}>
          {justJoined ? (
            <VoteConfirm closeJoinedHint={closeJoinedHint} />
          ) : user ? (
            <JoinAsLoggedInUser
              title="Join this community to upvote!"
              projectId={projectId}
              meId={user.id}
              onDone={onJoined}
            />
          ) : (
            <CreateAccountOverlay
              title="Join this community to upvote!"
              welcomeAtPath={window.location.pathname}
            />
          )}
        </ArrowOverlay>
      )}
    >
      <UpvoteButton upvoteInfo={upvoteInfo} card={card} />
    </DropDownForChild>
  );
};

const Upvotes = ({card, root}) => {
  const [justJoined, {on, off}] = useToggle();
  const {discord, upvotes} = card.meta;
  const [doVote] = useMutation("votes", "vote");
  const [doUnvote] = useMutation("votes", "unvote");

  const project = card.deck && card.deck.project;
  if (!project) return null;
  const projectId = project.$meta.get("id");
  if (!discord && !upvotes && !project.allowUpvotes) return null;

  const upvoteInfo = getUpvoteInfo({card, root});
  const {myUpvoteId, isLoaded} = upvoteInfo;
  const handleVote = () => {
    if (!myUpvoteId && root.loggedInUser) doVote({cardId});
  };
  const mayUpvote = () => {
    if (justJoined) return false;
    if (process.env.REACT_APP_MODE !== "open") return true;
    if (!root.loggedInUser) return false;
    return (
      projectId &&
      root.loggedInUser.$meta.first("publicProjectMembership", {projectId, $order: "createdAt"})
    );
  };
  const cardId = card.id;
  const cardCtx = card.deck && card.deck.$meta.get("id", null);
  return mayUpvote() ? (
    <UpvoteButton
      disabled={!isLoaded}
      onClick={
        project.allowUpvotes &&
        (() => (myUpvoteId ? doUnvote({id: myUpvoteId, cardId}) : doVote({cardId})))
      }
      upvoteInfo={upvoteInfo}
      card={card}
      active={myUpvoteId}
    />
  ) : (
    cardCtx && (
      <NonCommunityMemberUpvoteButton
        upvoteInfo={upvoteInfo}
        card={card}
        user={root.loggedInUser}
        projectId={projectId}
        onJoined={on}
        justJoined={justJoined}
        closeJoinedHint={off}
        onVote={handleVote}
        cardCtx={cardCtx}
      />
    )
  );
};

export default Upvotes;
